<template>
  <div class="conversation-list">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="d-flex justify-center align-center pa-4">
        <v-progress-circular indeterminate color="success" size="24" />
        <span class="ml-2 text-body-2">Cargando conversaciones...</span>
      </div>
    </div>
    
    <!-- Empty state -->
    <div v-else-if="conversations.length === 0" class="empty-state">
      <div class="text-center pa-6">
        <v-icon size="64" color="grey-lighten-2">mdi-chat-sleep</v-icon>
        <div class="text-body-1 text-grey mt-3 mb-2">No hay conversaciones</div>
        <div class="text-caption text-grey">
          Las conversaciones aparecen automáticamente cuando los pacientes escriben por WhatsApp
        </div>
        <v-btn
          variant="text"
          color="primary"
          class="mt-3"
          @click="$emit('refresh')"
        >
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>
    </div>
    
    <!-- Conversations list -->
    <v-list v-else class="conversation-items pa-0">
      <template v-for="conversation in conversations" :key="conversation.id">
        <v-list-item
          :class="[
            'conversation-item',
            { 'selected': selectedConversation?.id === conversation.id },
            { 'needs-attention': conversation.needs_attention }
          ]"
          @click="$emit('select', conversation)"
        >
          <!-- Avatar -->
          <template #prepend>
            <v-badge
              :content="conversation.unread_count || 0"
              :model-value="conversation.unread_count > 0"
              color="success"
              overlap
            >
              <v-avatar 
                size="48" 
                :color="conversation.avatar ? 'transparent' : getAvatarColor(conversation.patient_name)"
              >
                <img 
                  v-if="conversation.avatar" 
                  :src="conversation.avatar" 
                  :alt="conversation.patient_name"
                />
                <span v-else class="text-h6 font-weight-bold">
                  {{ getInitials(conversation.patient_name) }}
                </span>
              </v-avatar>
            </v-badge>
          </template>
          
          <!-- Content -->
          <div class="conversation-content">
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="conversation-name d-flex align-center">
                <v-chip
                  size="x-small"
                  color="primary"
                  variant="outlined"
                  class="mr-2 clinic-code-chip"
                  density="compact"
                >
                  {{ conversation.clinic_id }}
                </v-chip>
                {{ conversation.patient_name }}
              </div>
              <div class="conversation-time">
                {{ formatTime(conversation.last_message_time) }}
              </div>
            </div>
            
            <div class="d-flex align-center">
              <div class="conversation-preview flex-grow-1">
                <v-icon 
                  v-if="isLastMessageOutgoing(conversation)"
                  size="14" 
                  :color="getMessageStatusColor(conversation)"
                  class="mr-1"
                >
                  {{ getMessageStatusIcon(conversation) }}
                </v-icon>
                {{ truncateMessage(conversation.last_message) }}
              </div>
              
              <!-- Status indicators -->
              <div class="ml-2 d-flex align-center">
                <!-- Attention needed indicator (priority) -->
                <v-icon 
                  v-if="conversation.needs_attention"
                  size="16"
                  color="error"
                  class="mr-1 attention-alert"
                  title="Requiere atención urgente"
                >
                  mdi-alert-circle
                </v-icon>
                
                <!-- Unread indicator -->
                <v-icon 
                  v-if="conversation.unread_count > 0"
                  size="8"
                  color="success"
                  class="mr-1"
                >
                  mdi-circle
                </v-icon>
                
                <!-- Muted indicator -->
                <v-icon 
                  v-if="conversation.is_muted"
                  size="14"
                  color="grey"
                >
                  mdi-volume-mute
                </v-icon>
                
                <!-- Pin indicator -->
                <v-icon 
                  v-if="conversation.is_pinned"
                  size="14"
                  color="grey-darken-1"
                >
                  mdi-pin
                </v-icon>
              </div>
            </div>
          </div>
          
          <!-- Actions menu -->
          <template #append>
            <v-menu location="bottom end">
              <template #activator="{ props }">
                <v-btn
                  icon="mdi-chevron-down"
                  variant="text"
                  size="small"
                  v-bind="props"
                  class="conversation-menu-btn"
                  @click.stop
                />
              </template>
              <v-list density="compact">
                <v-list-item @click="markAsRead(conversation)">
                  <template #prepend>
                    <v-icon size="small">mdi-email-open</v-icon>
                  </template>
                  <v-list-item-title>Marcar como leído</v-list-item-title>
                </v-list-item>
                <v-list-item @click="togglePin(conversation)">
                  <template #prepend>
                    <v-icon size="small">
                      {{ conversation.is_pinned ? 'mdi-pin-off' : 'mdi-pin' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    {{ conversation.is_pinned ? 'Desanclar' : 'Anclar' }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="toggleMute(conversation)">
                  <template #prepend>
                    <v-icon size="small">
                      {{ conversation.is_muted ? 'mdi-volume-high' : 'mdi-volume-mute' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    {{ conversation.is_muted ? 'Activar' : 'Silenciar' }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="toggleAttention(conversation)">
                  <template #prepend>
                    <v-icon size="small" :color="conversation.needs_attention ? 'error' : 'grey'">
                      {{ conversation.needs_attention ? 'mdi-alert-circle-outline' : 'mdi-alert-circle' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    {{ conversation.needs_attention ? 'Marcar como atendida' : 'Requiere atención' }}
                  </v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item @click="deleteConversation(conversation)" class="text-error">
                  <template #prepend>
                    <v-icon size="small" color="error">mdi-delete</v-icon>
                  </template>
                  <v-list-item-title>Eliminar chat</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-list-item>
        
        <v-divider />
      </template>
    </v-list>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  conversations: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  selectedConversation: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['select', 'refresh'])

// Methods
const getInitials = (name) => {
  if (!name) return '?'
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()
}

const getAvatarColor = (name) => {
  const colors = [
    'red', 'pink', 'purple', 'deep-purple', 'indigo', 'blue',
    'light-blue', 'cyan', 'teal', 'green', 'light-green',
    'lime', 'amber', 'orange', 'deep-orange', 'brown', 'blue-grey'
  ]
  
  if (!name) return 'grey'
  
  const hash = name.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)
  
  return colors[Math.abs(hash) % colors.length]
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  
  const now = new Date()
  const messageTime = new Date(timestamp)
  const diffInHours = (now - messageTime) / (1000 * 60 * 60)
  
  if (diffInHours < 1) {
    const minutes = Math.floor(diffInHours * 60)
    return minutes < 1 ? 'ahora' : `${minutes}min`
  } else if (diffInHours < 24) {
    return messageTime.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })
  } else if (diffInHours < 48) {
    return 'ayer'
  } else {
    return messageTime.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit'
    })
  }
}

const truncateMessage = (message) => {
  if (!message) return 'Sin mensajes'
  return message.length > 50 ? message.substring(0, 50) + '...' : message
}

const isLastMessageOutgoing = (conversation) => {
  return conversation.last_message_direction === 'outgoing'
}

const getMessageStatusColor = (conversation) => {
  if (!isLastMessageOutgoing(conversation)) return 'grey'
  
  const status = conversation.last_message_status || 'sent'
  const colors = {
    sending: 'orange',
    sent: 'grey',
    delivered: 'grey',
    read: 'blue',
    failed: 'error'
  }
  return colors[status] || 'grey'
}

const getMessageStatusIcon = (conversation) => {
  if (!isLastMessageOutgoing(conversation)) return ''
  
  const status = conversation.last_message_status || 'sent'
  const icons = {
    sending: 'mdi-clock-outline',
    sent: 'mdi-check',
    delivered: 'mdi-check-all',
    read: 'mdi-check-all',
    failed: 'mdi-alert-circle'
  }
  return icons[status] || 'mdi-check'
}

const markAsRead = (conversation) => {
  // TODO: Implementar lógica para marcar como leído
}

const togglePin = (conversation) => {
  // TODO: Implementar lógica para anclar/desanclar
}

const toggleMute = (conversation) => {
  // TODO: Implementar lógica para silenciar/activar
}

const toggleAttention = async (conversation) => {
  // Necesitamos acceso al chat store
  const { useChatStore } = await import('@/stores/chat')
  const chatStore = useChatStore()
  
  const success = await chatStore.toggleConversationAttention(conversation.id)
  if (success) {
  } else {
    console.error('❌ Error cambiando estado de atención')
  }
}

const deleteConversation = (conversation) => {
  // TODO: Implementar lógica para eliminar conversación
}
</script>

<style scoped>
.conversation-list {
  flex: 1;
  overflow-y: auto;
}

.loading-state {
  padding: 20px 0;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
}

.conversation-items {
  height: 100%;
}

.conversation-item {
  padding: 12px 16px !important;
  border-left: 4px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;
}

.conversation-item:hover {
  background-color: #f5f5f5 !important;
}

.conversation-item.selected {
  background-color: #e3f2fd !important;
  border-left-color: #2196f3;
}

/* Conversation needs attention styling */
.conversation-item.needs-attention {
  background-color: #fef2f2 !important;
  border-left-color: #ef4444 !important;
}

.conversation-item.needs-attention:hover {
  background-color: #fee2e2 !important;
}

.conversation-item.needs-attention.selected {
  background-color: #fde8e8 !important;
  border-left-color: #dc2626 !important;
}

/* Attention alert animation */
.attention-alert {
  animation: pulse-attention 2s infinite;
}

@keyframes pulse-attention {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.conversation-content {
  flex: 1;
  min-width: 0;
  padding-left: 12px;
}

.conversation-name {
  font-weight: 500;
  font-size: 14px;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.clinic-code-chip {
  font-size: 10px !important;
  height: 18px !important;
  flex-shrink: 0;
}

.conversation-time {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.conversation-preview {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}

.conversation-menu-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.conversation-item:hover .conversation-menu-btn {
  opacity: 1;
}

/* Scrollbar personalizada */
.conversation-list::-webkit-scrollbar {
  width: 6px;
}

.conversation-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.conversation-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.conversation-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>